import React, {useEffect, useState} from "react";
import {FormControlLabel, Switch, TextField, RadioGroup, FormControl, FormLabel, Radio} from "@mui/material";
import FunctionsData from "../../Functions/FunctionsData";
import LandingAutoComplete from "../LandingAutoComplete";
import BCTIContractModal from "./BCTIContractModal";

/*
	 Show detail
	 @param {number} vet_id
 */
export default function VetsDetails({vet_id, optoutPublic, setOptoutPublic, optoutCommunity, setOptoutCommunity,optoutMicroChipOnly,setOptoutMicroChipOnly}) {

	const [data,setData] = useState(false);
	const [state,setState] = useState(false);
	const [dataLoading,setDataLoading] = useState(true);
	const [optInBCTI, setOptInBCTI] = useState(false);
	const [showBCTIModal, setShowBCTIModal] = useState(false);
	// No default selection; empty string means none chosen yet
	const [bctiPaymentTerm, setBctiPaymentTerm] = useState('');
	// When user toggles the switch on we open the modal — track pending state to revert if cancelled
	const [pendingBctiOptIn, setPendingBctiOptIn] = useState(false);
	// Track vendor status refresh
	const [vendorLoading, setVendorLoading] = useState(false);
	const [vendorRec, setVendorRec] = useState(null);
	const [vendorSystemId, setVendorSystemId] = useState(null);
	const companyId = process.env.BC_COMPANY_ID || 'e3e5b3a8-b3ba-f011-bbd1-000d3a6af784';

	function refreshVendorStatus(mercoId, vetId) {
		if(!mercoId) return;
		if(!companyId || companyId === 'PLACEHOLDER_COMPANY_ID') {
			console.warn('Missing BC_COMPANY_ID env var; vendor status not fetched');
			return;
		}
		setVendorLoading(true);
		const path = FunctionsData.getApiUrl(`/api/merco/companies/${companyId}/vendors?$filter=no eq '${mercoId}'`);
		// Use existing helper so CSRF token and session token are provided consistently
		FunctionsData.fetchDataGetNoLoading(path, (result) => {
			let rec = null;
			if(Array.isArray(result)) rec = result[0];
			else if(result) rec = result;
			if(rec) {
				setVendorRec(rec);
				// attempt to extract vendor system id from common fields
				const sysId = rec.id || rec.systemId || rec.systemid || rec.vendorId || rec.systemID || rec.ID;
				if(sysId) setVendorSystemId(sysId);
				const val = rec.spcaBCTIVendor;
				const enabled = (val === true || val === 1 || val === '1' || String(val).toLowerCase() === 'true');
				setOptInBCTI(enabled);
				// Determine payment term: prefer explicit vendor field, else default to 30D when enabled
				if(rec.spcaBCTIPaymentTerms) {
					const term = String(rec.spcaBCTIPaymentTerms) === '7D' ? '7D' : 'ME+20D';
					setBctiPaymentTerm(term);
				} else if (enabled) {
					setBctiPaymentTerm('ME+20D');
				} else {
					setBctiPaymentTerm('');
				}
			}
			setVendorLoading(false);
		}).catch(err => {
			console.error('Vendor status fetch error (helper)', err);
			setVendorLoading(false);
		});
	}

	function updateToggleValue(field, value) {		
		FunctionsData.postData(
			FunctionsData.getApiUrl('/api/vet/') + vet_id,
			{ [field]: value ? 0 : 1 } 
		).then(() => {
			setDataLoading(true);
		});
	}	

	// Update BCTI fields on the vet record itself
	function updateVetBcti(spcaBctiVendorValue, termsValue) {
		const payload = {
			spcaBCTIVendor: spcaBctiVendorValue
		};
		if (typeof termsValue !== 'undefined') {
			payload.terms = termsValue;
		}
		FunctionsData.postData(
			FunctionsData.getApiUrl('/api/vet/') + vet_id,
			payload
		).catch(err => {
			console.error('Failed to update vet BCTI fields', err);
		});
	}

	useEffect(() => {
		FunctionsData.fetchDataGet(
			FunctionsData.getApiUrl('/api/vet/')+vet_id,
			setData,dataLoading,setDataLoading
		)
	}, [dataLoading]);

	useEffect(() => {
		if(!data) return;
		let row = null;
		if(Array.isArray(data)) {
			if(data.length === 0) return;
			row = data[0];
		} else if (typeof data === 'object') {
			row = data;
		}
		if(!row) return;
		setState({...row});
		setOptoutPublic(row.optout_public === 0);
		setOptoutCommunity(row.optout_community === 0);
		setOptoutMicroChipOnly(row.optout_microchiponly===0);
		// Fetch vendor status using vendor_id (if available)
		if(row.vendor_id) {
			refreshVendorStatus(row.vendor_id,row.id);
		}
	}, [data]);

	const fields = [		
		{slug:'Cat', type:'switch', label:'Available for Cat procedures', animalExclusions:true},
		{slug:'Dog', type:'switch', label:'Available for Dog procedures', animalExclusions:true},
		{slug:'business_name', type:'string', label:'Clinic name'},
		{slug:'address', type:'address', label:'Clinic address'},
		{slug:'email', type:'string', label:'Clinic email', validationEmail:true},
		{slug:'email_accounts', type:'string', label:'Finance email', validationEmail:true},
		{slug:'phone', type:'string', label:'Phone number'},
		{slug:'supplier_website', type:'string', label:'Website'},
		{slug:'supplier_terms', type:'string', label:'Terms & conditions (Public site)', multiline:true},
		{slug:'supplier_terms_community', type:'string', label:'Terms & conditions (Community site)', multiline:true},
	]

	return (
		<div style={{width:'100%'}}>
			{/* BCTI contract modal */}
			<BCTIContractModal
				open={showBCTIModal}
				onClose={() => {
					// if user cancels and this was a pending opt-in, revert the switch and payment term
					setShowBCTIModal(false);
					if(pendingBctiOptIn) {
						setOptInBCTI(false);
						setBctiPaymentTerm('');
						setPendingBctiOptIn(false);
					}
				}}
				vet_id={vet_id}
				paymentTerm={bctiPaymentTerm}
				onSaved={() => { setDataLoading(true); setOptInBCTI(true); setPendingBctiOptIn(false); }}
			/>
			<div style={{ marginBottom: 20 }}>
				{/* BCTI opt-in switch */}
				<div style={{marginBottom:12}}>
					<FormControlLabel
						control={
							<Switch
								checked={optInBCTI}
								onChange={(e) => {
									if (e.target.checked) {
										// User has enabled the switch — default to 30D and open modal to sign
										setOptInBCTI(true);
										setBctiPaymentTerm('ME+20D');
										setPendingBctiOptIn(true);
										// Persist to vets table (spcaBCTIVendor + terms)
										updateVetBcti(1, 'ME+20D');
										// Persist to MERCO vendor API if we have vendorSystemId
										if(vendorSystemId) {
											setVendorLoading(true);
											FunctionsData.patchData(FunctionsData.getApiUrl(`/api/merco/companies/${companyId}/vendors/${vendorSystemId}/spcaBCTIVendor`), { spcaBCTIVendor: 1, spcaBCTIPaymentTerms: 'ME+20D' })
												.then(() => console.log('Vendor opt-in patched'))
												.catch(err => console.error('Vendor opt-in patch failed', err))
												.finally(() => setVendorLoading(false));
										} else {
											console.warn('vendorSystemId not available; will not patch opt-in now');
										}
										setShowBCTIModal(true);
										} else {
										// Opt out immediately
										FunctionsData.postData(
											FunctionsData.getApiUrl('/api/vet/')+vet_id,
											{ bcti_optin: 0 }
										).then(() => setDataLoading(true));
										setOptInBCTI(false);
										setBctiPaymentTerm('');
											// Update vets table BCTI fields
											updateVetBcti(0, '');
										// also patch vendor opt-out if possible
										if(vendorSystemId) {
											setVendorLoading(true);
											FunctionsData.patchData(FunctionsData.getApiUrl(`/api/merco/companies/${companyId}/vendors/${vendorSystemId}/spcaBCTIVendor`), { spcaBCTIVendor: 0 })
												.then(() => console.log('Vendor opt-out patched'))
												.catch(err => console.error('Vendor opt-out patch failed', err))
												.finally(() => setVendorLoading(false));
										}
									}
								}}
							/>
					}
					label="BCTI (Buyer Created Tax Invoice)"
					/>
				</div>
				{optInBCTI && (
					<div style={{marginBottom:20}}>
						<FormControl component="fieldset">
							<FormLabel component="legend">Payment Terms {vendorLoading && '(loading...)'}</FormLabel>
							<RadioGroup row value={bctiPaymentTerm} onChange={(e)=> {
								const val = e.target.value;
								setBctiPaymentTerm(val);
									// Persist current toggle + terms to vets table
									updateVetBcti(optInBCTI ? 1 : 0, val);
								// Do NOT open modal on radio change. Persist change to MERCO vendor API if we have system id
								(async () => {
									if(!vendorSystemId) {
										console.warn('No vendorSystemId available; cannot PATCH payment term');
										return;
									}
									setVendorLoading(true);
									try {
										const payload = { spcaBCTIPaymentTerms: val };
										await FunctionsData.patchData(FunctionsData.getApiUrl(`/api/merco/companies/${companyId}/vendors/${vendorSystemId}/spcaBCTIVendor`), payload);
									} catch(err) {
										console.error('Failed to PATCH payment term', err);
									} finally {
										setVendorLoading(false);
									}
								})();
							}}>
									<FormControlLabel value="ME+20D" control={<Radio />} label="Monthly (current)" />
								<FormControlLabel value="7D" control={<Radio />} label="7 days" />
							</RadioGroup>
						</FormControl>
					</div>
				)}
				<FormControlLabel
					control={
						<Switch
							checked={optoutPublic}
							onChange={(e) => {setOptoutPublic(e.target.checked);
							updateToggleValue("optout_public", e.target.checked);								
					}}					
						/>
					}
					label="Snip'n'chip"
				/>
				</div>
			<div style={{ marginBottom: 20 }}>
				<FormControlLabel
					control={
						<Switch
							checked={optoutCommunity}
							onChange={(e) => {setOptoutCommunity(e.target.checked);
							updateToggleValue("optout_community", e.target.checked);
							}}
						/>
					}
					label="Community desexing"
				/>
			</div>
			{
				fields.map((f,index) => {
					if(f.type === 'string')
						return (
							<ShowFormDetailString vet_id={vet_id} key={index} row={f} state={state} setState={setState}/>
						)

					if(f.type === 'switch') {
						// Render the MicroChip Only toggle immediately after the Dog switch
						if(f.slug === 'Dog') {
							return (
								<div key={index}>
									<ShowFormDetailSwitch vet_id={vet_id} row={f} state={state} setState={setState} setDataLoading={setDataLoading} animalExclusions={f.animalExclusions} />
									<div style={{ marginBottom: 20 }}>
										<FormControlLabel
											control={
												<Switch
													checked={optoutMicroChipOnly === true}
													onChange={(e) => {setOptoutMicroChipOnly(e.target.checked);
														updateToggleValue("optout_microchiponly", e.target.checked);
													}}
												/>
											}
											label="MicroChip Only"
										/>
									</div>
								</div>
							)
						}
						return (
							<ShowFormDetailSwitch vet_id={vet_id} key={index} row={f} state={state} setState={setState} setDataLoading={setDataLoading} animalExclusions={f.animalExclusions}  />
						)
					}

					if(f.type === 'address')
						return (
							<ShowFormDetailAddress vet_id={vet_id} key={index} row={f} state={state} setState={setState} setDataLoading={setDataLoading} />
						)

					return null;

				})
			}
		</div>
	)
}

function validateEmail(email) {
	const re = /\S+@\S+\.\S+/;
	return re.test(email) ;
}

/*
	 Show detail
	 @param {number} vet_id
	 @param {object} row
	 @param {object} state
	 @param {function} setState
 */
const ShowFormDetailString=({vet_id,row,state, setState}) => {

	const [msg,setMsg] = useState(false)

	function upd(e) {
		FunctionsData.postData(
			FunctionsData.getApiUrl('/api/vet/')+vet_id,
			{[row.slug]:e.target.value}
		).then(() => {
			setMsg(true);
			setTimeout(() => {
				setMsg(false);
			}, 5000)
		})
	}

	return(
		<div style={{marginBottom:20, width:'100%'}}>
			<TextField label={row.label} value={state[row.slug]}
			           onChange={(e) => setState({...state,[row.slug]:e.target.value})}
			           onBlur={(e) => {
				           if(!row.validationEmail || validateEmail(state[row.slug])) {
					           upd(e)
				           }
			           }}
			           multiline={(row.multiline)}
			           rows={(row.multiline) ? 10 : 1}
			           focused
			           fullWidth
			           error={!(!row.validationEmail || validateEmail(state[row.slug]))}
			/>
			{
				(msg) ? <div><small>Item updated</small></div> : null
			}
		</div>
	)
}

/*
	 	Address
	 	@param {number} vet_id
	 	@param {object} state
	 	@param {function} setDataLoading

 */
const ShowFormDetailAddress=({vet_id,state,setDataLoading}) => {

	function setGooglePlace(row) {
		console.log('setGooglePlace',row)
		FunctionsData.postData(
			FunctionsData.getApiUrl('/api/vet/')+vet_id,
			{
				formatted_address:row.address,
				street:row.STREET_NUMBER + ' ' + row.STREET_NAME,
				suburb:row.SUBURB_NAME,
				city: row.TOWN_CITY_MAILTOWN,
				postcode:row.POSTCODE,
				dpid:row.DPID,
			}
		).then(() => setDataLoading(true))
	}

	return (
		<div style={{marginBottom:20}}>
			<LandingAutoComplete row={state} setGooglePlace={setGooglePlace}  />
		</div>
	)
}

/*
 	Switch
 	@param {number} vet_id
 	@param {object} row
 	@param {object} state
 	@param {function} setState
 	@param {function} setDataLoading

 */
const ShowFormDetailSwitch=({vet_id,row,state, setState, setDataLoading, animalExclusions}) => {

	const [excludedAnimals,setExcludedAnimals] = useState(String(row['excluded_animals']).split(',').map(r => r !== 'null') ?? []);

	useEffect(() => {
		setExcludedAnimals(String(state['excluded_animals']).split(',').filter(r => r !== 'null') ?? [])
	},[row])

	function upd(val) {

		console.log('excludedAnimals', state['excluded_animals'], excludedAnimals)
		if(animalExclusions) {

			let s = excludedAnimals;
			if(s?.includes(row.slug)) {
				s = s.map((r) => r !== row.slug);
			} else{
				s.push(row.slug);
			}

			s = [...new Set(s.filter(r => r !== 'true'))];

			FunctionsData.postData(
				FunctionsData.getApiUrl('/api/vet/')+vet_id,
				{excluded_animals:s.join(',')}
			).then(() => {
				setDataLoading(true)
			})

		}else{
			FunctionsData.postData(
				FunctionsData.getApiUrl('/api/vet/')+vet_id,
				{[row.slug]:val}
			).then(() => {
				setDataLoading(true)
			})
		}
	}

	return(
		<div style={{marginBottom:20, width:'100%'}}>
			<FormControlLabel control={
				<Switch checked={!animalExclusions ? (state[row.slug] === 0) : !excludedAnimals?.includes(row.slug)}
				        onChange={(e) => {
							if(!animalExclusions) {
								setState({...state, [row.slug]: (e.target.checked) ? 0 : 1});
								upd((e.target.checked) ? 0 : 1)
							}else{
								upd((e.target.checked) ? 0 : 1)
							}
				        }}
				/> } label={row.label} />
		</div>
	)
}
